import { ChangeEventHandler, FormEvent, useEffect, useState } from "react";
import dayjs from "dayjs";
import Joi from "joi";
import { ValidationUtil } from "utils";
import { FormWithQuestion } from "types/form";
import { SendOutPulseSurveyRequest } from "types/feedback-request";
import { FieldErrors } from "types/field-errors.type";
import {
  getDateFieldValueWithTimezone,
  getDateTimeWithTimezone,
} from "utils/due-date-form";
import { Button, DateField } from "components/common/forms";
import { ConfirmModal } from "components/common/modals";
import { toastError } from "utils/toast";
import { sendOutPulseSurveySchema } from "schemas";

type SendOutPulseFormProps = {
  selectedForm?: FormWithQuestion;
  onSubmit: (data: SendOutPulseSurveyRequest) => Promise<void>;
  isSubmitting?: boolean;
  setValidationError: (validationError: object) => void;
  validationError: FieldErrors<SendOutPulseSurveyRequest>;
};

const SendOutPulseForm: React.FC<SendOutPulseFormProps> = ({
  selectedForm,
  onSubmit,
  isSubmitting,
  setValidationError,
  validationError,
}) => {
  const [showConfirmSendModal, setShowConfirmSendModal] = useState(false);

  const [formValues, setFormValues] = useState<
    Partial<SendOutPulseSurveyRequest>
  >({});

  useEffect(() => {
    const defaultDueDate = dayjs().add(7, "days").toDate();
    setFormValues({
      formId: selectedForm?.id,
      dueDate: selectedForm?.dueDate || defaultDueDate,
    });
  }, [selectedForm]);

  const handleSubmit = async () => {
    const { error, value: validData } = validateForm();

    if (!error) {
      await onSubmit(validData);
    }

    handleCloseConfirmModal();
  };

  const validateForm = (): Joi.ValidationResult<SendOutPulseSurveyRequest> => {
    const dueDateChanged =
      selectedForm?.dueDate?.getTime() !== formValues.dueDate?.getTime();

    const { error, value } = sendOutPulseSurveySchema.validate(
      {
        formId: formValues.formId,
        dueDate: formValues.dueDate,
      },
      {
        context: { dueDateChanged: dueDateChanged },
      },
    );

    if (error) {
      const newValidationError = ValidationUtil.transformValidationError(error);
      setValidationError(newValidationError);
      toastError(error.message);
    }
    return {
      error,
      value: value as SendOutPulseSurveyRequest,
    };
  };

  const handleShowConfirmSendModal = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { error } = validateForm();
    if (!error) {
      setShowConfirmSendModal(true);
    }
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmSendModal(false);
  };

  const handleClearErrorValidation = () => {
    setValidationError({});
  };

  const onDueDateChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const dueDate = getDateTimeWithTimezone(e.target.value).toDate();

    setFormValues({
      ...formValues,
      dueDate,
    });
  };

  return (
    <form
      className="flex-1 flex flex-col gap-8"
      onSubmit={handleShowConfirmSendModal}
    >
      <DateField
        id="form-due-date"
        label="Deadline"
        hint="Give this survey a deadline"
        value={
          formValues.dueDate
            ? getDateFieldValueWithTimezone(formValues.dueDate)
            : ""
        }
        onChange={onDueDateChange}
        name="dueDate"
        className="mb-0"
        errorMessage={validationError?.dueDate}
        onClearError={handleClearErrorValidation}
      />

      <Button
        label="Send to All"
        className="w-full"
        type="submit"
        size="large"
      />

      <ConfirmModal
        open={showConfirmSendModal}
        title="Send out form"
        content="Are you sure you want to send this form to users?"
        yesButton="Yes"
        noButton="No"
        onYes={handleSubmit}
        onNo={handleCloseConfirmModal}
        yesButtonLoading={isSubmitting}
      />
    </form>
  );
};

export default SendOutPulseForm;
